import React, { useCallback, useEffect, useState } from "react";
import { generateSignedUrl } from "../../../utils/getPreSignedURL";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Typography
} from "@mui/material";
import AudioPlayer from "../AudioPlayer";
import useSignedUrl from "../../../utils/useSignedUrl";
import useSanitizeContent from "../../../utils/useSanitizeContent";
import "./RenderHtml.css";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  completeTrainings,
  getCurrentUserLevels,
  updatePercentage,
  updateTrainingById
} from "../../../features/training/trainingSlice";
import {
  selectLevelData,
  selectLoading
} from "../../../features/training/trainingSelectors";
import useThemeColor from "../../../utils/useTheme";
import {
  addNotificationUser,
  fetchCurrentUser,
  getNotificationUser,
  updateUserUncompletedCount
} from "../../../features/auth/authSlice";
import { Help } from "@mui/icons-material";
import FAQModal from "../../Modals/FAQModal";
import ReactConfetti from "react-confetti";
function convertLinks(htmlString) {
  // Create a temporary container
  const container = document.createElement("div");
  container.innerHTML = htmlString;

  // Function to process text nodes
  function processTextNodes(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      // URL regex pattern
      const urlRegex = /(https?:\/\/[^\s<>"']+)/gi;

      // If the text contains a URL
      if (urlRegex.test(node.textContent)) {
        // Create a temporary span to hold the new content
        const span = document.createElement("span");

        // Replace URLs with anchor tags
        span.innerHTML = node.textContent.replace(urlRegex, (url) => {
          return `<a href="${url}">${url}</a>`;
        });

        // Replace the text node with the new content
        const parent = node.parentNode;
        while (span.firstChild) {
          parent.insertBefore(span.firstChild, node);
        }
        parent.removeChild(node);
      }
    } else if (node.nodeType === Node.ELEMENT_NODE && node.nodeName !== "A") {
      // Process child nodes if this is not an anchor element
      Array.from(node.childNodes).forEach(processTextNodes);
    }
  }

  // Process all nodes in the container
  Array.from(container.childNodes).forEach(processTextNodes);

  return container.innerHTML;
}
const AudioItem = ({ audio, setIsAudioFinished }) => {
  const src = useSignedUrl(
    audio,
    process.env.REACT_APP_BACKEND_TRAINING_IMAGE_LOCATION
  );
  const styles = useThemeColor();
  return (
    <Box
      sx={{
        background: styles.inputLabelColor,
        width: { sm: "100%" }
      }}
      // p={"8px 20px"}
      borderRadius={"100px"}
    >
      <AudioPlayer
        setIsAudioFinished={setIsAudioFinished}
        styles={styles}
        isSliderNeeded
        src={src}
      />
    </Box>
  );
};
const isValidUrl = (url) => {
  return /^(https?:\/\/|www\.)/.test(url);
};
const RenderHtml = ({
  activeData,
  styles,
  activeDay,
  setIsAudioFinished,
  isAudioFinished,
  isMobile,
  selectedLevel,
  currentLevel,
  activeLevelForMobile
}) => {
  const [value, setValue] = useState("");
  const availableLevels = useSelector(selectLevelData);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openFAQModal, setOpenFAQModal] = useState(false);
  const [loadingHtml, setLoadingHtml] = useState(false);
  const loading = useSelector(selectLoading);
  const updateImageSources = useCallback(
    async (html, color) => {
      setLoadingHtml(true);
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const images = Array.from(doc.querySelectorAll("img"));

      for (let img of images) {
        const fileName = img.getAttribute("src"); // Assuming the src contains the filename

        if (fileName && !fileName.startsWith("http")) {
          try {
            const signedUrl = await generateSignedUrl(
              fileName,
              process.env.REACT_APP_BACKEND_TRAINING_IMAGE_LOCATION
            );

            // Replace filename with the signed URL in the img tag
            img.src = signedUrl;
          } catch (error) {
            console.error("Error generating signed URL:", error);
          }
        }
        if (!img.hasAttribute("width")) {
          img.setAttribute("width", "100%");
        }
        if (!img.hasAttribute("height")) {
          img.setAttribute("height", "100%");
        }
      }

      const links = Array.from(doc.querySelectorAll("a"));
      links.forEach((link) => {
        link.target = "_blank";
        let href = link.getAttribute("href");
        link.style.background = ""; // Clear background
        link.style.color = ""; //
        if (href) {
          if (href.startsWith("www.")) {
            link.setAttribute("href", `https://${href}`);
          } else if (!isValidUrl(href)) {
            // Optionally prepend a default protocol (http or https)
            link.setAttribute("href", `https://${href}`);
          }
        }
      });
      const paragraphs = [
        ...Array.from(doc.querySelectorAll("span")),
        ...Array.from(doc.querySelectorAll("strong")),
        ...Array.from(doc.querySelectorAll("em")),
        ...Array.from(doc.querySelectorAll("ul")),
        ...Array.from(doc.querySelectorAll("b")),
        ...Array.from(
          doc.querySelectorAll(["h1", "h2", "h3", "h4", "h5", "h6"])
        )
      ];
      paragraphs.forEach((p) => {
        p.style.background = ""; // Clear background
        p.style.color = color; // Clear color
      });

      const allParas = Array.from(doc.querySelectorAll("p"));
      let consecutiveJustifyBrs = []; // Array to track sequences of <p> elements

      allParas.forEach((p, index) => {
        // Check if it's a target <p> element
        if (
          p.classList.contains("ql-align-justify") &&
          p.innerHTML.trim() === "<br>"
        ) {
          consecutiveJustifyBrs.push(p);
        } else {
          // If the sequence ends, check its length and remove if needed
          if (consecutiveJustifyBrs.length > 7) {
            const lastFourElements = consecutiveJustifyBrs.slice(-4);
            lastFourElements.forEach((element) => element.remove());
          }
          // Reset the sequence tracking array
          consecutiveJustifyBrs = [];
        }
      });

      // Handle any remaining sequence at the end of the loop
      if (consecutiveJustifyBrs.length > 7) {
        const lastFourElements = consecutiveJustifyBrs.slice(-4);
        lastFourElements.forEach((element) => element.remove());
      }
      // Set the updated HTML back to the editor
      setValue(convertLinks(doc.body.innerHTML));
      // setValue(doc.body.innerHTML);
      setLoadingHtml(false);
    },
    [setValue]
  );
  useEffect(() => {
    const initialHtml =
      activeData?.generalText || "<p></p>";
    updateImageSources(initialHtml, styles.color);
  }, [updateImageSources, activeDay, activeData?.generalText]);
  const sanitizedContent = useSanitizeContent(value);
  const dispatch = useDispatch();

  const scrollToTop = useCallback(() => {
    // Wait for the next tick to ensure DOM updates have occurred
    setTimeout(() => {
      const scrollableElement = document.getElementById("current-day");
      if (scrollableElement) {
        scrollableElement.scrollIntoView({
          behavior: "instant",
          block: "start"
        });
      }
    }, 100);
  }, []);

  const [showConfetti, setShowConfetti] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const onComplete = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      const completeResult = await dispatch(
        completeTrainings({
          date: dayjs().format("YYYY-MM-DD"),
          levelId: activeData?.levelId,
          trainingDayId: activeData?.trainingId
        })
      );
      if (completeResult?.payload?.missedDays?.length) {
        setSnackbarMessage(
          `Please complete all prior trainings before this day. Missed Days: ${completeResult?.payload?.missedDays.join(
            ", "
          )}.`
        );
        setOpenSnackbar(true);
        return;
      }
      // Check if the first dispatch was rejected
      if (completeResult.meta.requestStatus === "rejected") {
        throw new Error("completeTrainings failed");
      }

      // Show confetti after modal closes
      setShowConfetti(true);

      // Remove confetti after 5 seconds
      setTimeout(() => {
        setShowConfetti(false);
      }, 3000);

      const levelData = availableLevels?.find(
        (item) => item.levelId === activeData?.levelId
      );

      const updatedPercent =
        ((levelData?.completedTrainingDays + 1) /
          levelData?.totalTrainingDays) *
        100;

      await dispatch(
        updatePercentage({
          id: activeData?.levelId,
          updates: {
            trainingCompletionPercentage: updatedPercent,
            completedTrainingDays: levelData?.completedTrainingDays + 1
          }
        })
      );
      await dispatch(updateUserUncompletedCount());
      if (updatedPercent === 100) {
        const currentDate = dayjs().format("YYYY-MM-DD");
        dispatch(addNotificationUser(currentDate));
        dispatch(fetchCurrentUser(currentDate));
        dispatch(getCurrentUserLevels(currentDate));
      }
      // Only run this if the first dispatch succeeded
      setTimeout(() => {
        dispatch(
          updateTrainingById({
            id: activeData?.trainingId,
            updates: { completedOn: dayjs().format("YYYY-MM-DD HH:mm:ss") }
          })
        );
      }, 5000);
      setIsAudioFinished(false);
      scrollToTop();
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isAudioFinished && !activeData?.completedOn) onComplete();
  }, [isAudioFinished, activeData]);

  return (
    <Box id="current-day" display={"flex"} flexDirection="column" gap="10px">
      <Typography fontWeight={600} fontSize="28px">
        {activeData?.title}
      </Typography>

      <Typography fontSize={"18px"}>{activeData?.subTitle}</Typography>

      {/* Fixed container for the audio player */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          // backgroundColor: styles.backgroundColor || "#fff",
          paddingBottom: "10px",
          paddingTop: "10px",
          width: "100%"
        }}
      >
        <AudioItem
          setIsAudioFinished={setIsAudioFinished}
          audio={activeData?.audioFile}
        />
      </Box>

      {/* Content section with appropriate padding */}
      <Box
        sx={{
          marginTop: "10px", // Add spacing after the fixed AudioItem
          width: "100%",
          overflow: "auto"
        }}
      >
        {loadingHtml || loading ? (
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            my={10}
          >
            <CircularProgress
              sx={{ width: "100%", justifyContent: "center" }}
            />
          </Box>
        ) : sanitizedContent || sanitizedContent !== "undefined" ? (
          <div
            className="preview-html"
            style={{
              ".ql-align-center": {
                display: "flex",
                justifyContent: "center"
              },
              p: {
                color: `${styles.color} !important`
              }
            }}
            dangerouslySetInnerHTML={{
              __html: sanitizedContent ? sanitizedContent : <div />
            }}
          />
        ) : (
          <></>
        )}
      </Box>

      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {showConfetti && (
        <ReactConfetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={isMobile ? 600 : 1200}
          recycle={false}
          colors={["#368AD0", "#2a6ba4", "#1d4d7a", "#62a6e0", "#90c1e8"]}
          onConfettiComplete={() => setShowConfetti(false)}
        />
      )}
      {activeData ? (
        <Button
          variant="contained"
          color="primary"
          onClick={onComplete}
          disabled={activeData?.completedOn}
          sx={{
            marginTop: 3.1,
            backgroundColor: "#368AD0",
            cursor: "pointer",
            color: "#fff",
            width: "100%",
            height: "42px",
            fontWeight: "bold",
            textTransform: "none",
            ":disabled": {
              backgroundColor: "#A7C8E9",
              color: "#64AEDD",
              pointerEvents: "none"
            }
          }}
        >
          Complete
        </Button>
      ) : (
        <></>
      )}
      {(isMobile ? selectedLevel && activeLevelForMobile : selectedLevel) && (
        <Box
          sx={{
            position: "fixed",
            display: "flex",
            gap: "20px",
            width: "50%",
            bottom: { xs: 95, sm: 20 },
            zIndex: { xs: 100000, md: "unset" }
          }}
        >
          <IconButton
            variant="contained"
            sx={{
              display: "flex",
              backgroundColor: "#FF4000",
              color: "#FCFCFC",
              "&:hover": {
                backgroundColor: "#FF7043"
              }
            }}
            onClick={() => setOpenFAQModal((prev) => !prev)}
          >
            <Help />
          </IconButton>
          <FAQModal
            htmlValue={currentLevel?.faq}
            open={openFAQModal}
            onClose={() => setOpenFAQModal(false)}
          />
        </Box>
      )}
    </Box>
  );
};

export default RenderHtml;
